import React, { useEffect, useRef } from 'react'
import { getAnonSession } from '@fs/zion-session'
import useEP from '../../hooks/useEP'
import {
  authorizeAzureUser,
  getAdditionalAzureDetails,
  getDiscoveryUserFromAzure,
} from '../../../wss/global/helpers/azureAuthServices'
import useDSApplicationMode from '../../hooks/useDSApplicationMode'
import setCookie from '../../helpers/cookie'

import {
  getExperienceDataStatus,
  getTakeAways,
  uploadTakeAway,
  logoutDiscoveryStation,
  getDiscoveryUser,
} from './services'

export const useExperienceDataStatus = (azUSID) => {
  const [data, setData] = React.useState([])
  const [forceRedraw, setForceRedraw] = React.useState(0)

  React.useEffect(() => {
    getExperienceDataStatus(azUSID).then((results) => {
      const isComplete = results.getPersonAncestry === 'Complete'
      if (!isComplete) {
        // Keep making this call until all the experience data shows as complete
        setTimeout(() => {
          setForceRedraw(forceRedraw + 1)
        }, 2000)
      }

      setData(results, forceRedraw)
    })
  }, [azUSID, forceRedraw])

  return data
}

export const useLogoutDiscoveryStation = (azUSID) => {
  const [data, setData] = React.useState([])

  React.useEffect(() => {
    logoutDiscoveryStation(azUSID).then((results) => {
      setData(results)
    })
  }, [azUSID])

  return data
}

export const useUploadTakeAway = () => {
  const [state, dispatch, func] = useEP(uploadTakeAway)
  return [state, func, dispatch]
}

export const useTakeAwaysData = (azUSID) => {
  const [data, setData] = React.useState([])

  useEffect(() => {
    getTakeAways(azUSID).then((results) => {
      setData(results)
    })
  }, [azUSID])

  return data
}

export const useKioskIdle = (secondsToTimeout, callback) => {
  const savedCallback = useRef(callback)
  const savedTimer = useRef()
  const watchedActions = ['click', 'touchstart', 'mousemove']
  // Set up the interval.
  useEffect(() => {
    const tick = () => {
      savedCallback.current()
    }

    const createNewTimer = () => {
      const id = setTimeout(tick, secondsToTimeout * 1000)
      return id
    }

    const resetIdleTimeout = () => {
      clearTimeout(savedTimer.current)
      savedTimer.current = createNewTimer()
    }

    watchedActions.forEach((evt) => document.addEventListener(evt, resetIdleTimeout, false))

    if (secondsToTimeout !== null) {
      savedTimer.current = createNewTimer()
      return () => {
        watchedActions.forEach((evt) => document.removeEventListener(evt, resetIdleTimeout, false))
        clearTimeout(savedTimer.current)
      }
    }
    return true
  }, [secondsToTimeout, watchedActions])
}

export const useDiscoveryUser = () => {
  const [data, setData] = React.useState([])

  useEffect(() => {
    getDiscoveryUser().then((results) => {
      setData(results)
    })
  }, [])

  return data
}

export const useDiscoveryAzureUser = () => {
  const [data, setData] = React.useState([])
  const azureSessionId = sessionStorage.getItem('userSessionId')
  useEffect(() => {
    getDiscoveryUserFromAzure(azureSessionId).then((results) => {
      setData(results)
    })
  }, [azureSessionId])

  return data
}

export const useCheckForUnauthAzureSession = ({ dispatch, nonstandardUserInitialized }) => {
  const mode = useDSApplicationMode()
  const isAzureSession = mode === 'STATION' || mode === 'KIOSK'

  useEffect(() => {
    if (isAzureSession && !nonstandardUserInitialized) {
      const azureSessionId = sessionStorage.getItem('userSessionId')
      authorizeAzureUser(azureSessionId).then((auth) => {
        if (!auth.authenticated) {
          getAnonSession().then(() => {
            // getAnonSession will set an fssession id to the cookies
            getAdditionalAzureDetails({
              azureBearerToken: auth.bearerToken,
              azureSessionId,
              dispatch,
            })
          })
        } else {
          setCookie('fssessionid', auth.accessToken)
          getAdditionalAzureDetails({
            azureBearerToken: auth.bearerToken,
            azureSessionId,
            dispatch,
          })
        }
      })
    }
  }, [dispatch, isAzureSession, nonstandardUserInitialized])
}
